import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the EmploymentTypeMasterContext
const LeaveTypeMasterContext = createContext();

// Custom hook to use the EmploymentTypeMasterContext
export const useLeaveType = () => useContext(LeaveTypeMasterContext);

// Provider component to wrap the app
export const LeaveTypeMasterProvider = ({ children }) => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const API_URL = "https://hrmspayroll.prepstripe.com/api/masters/leave-types"; // Replace with your actual API URL
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // const fetchLeaveTypes = async (page = 1) => {
  //   try {
  //     const response = await axios.get(API_URL, {
  //       headers: {
  //         Authorization: `Bearer ${getToken()}`,
  //       },
  //     });
  //     setLeaveTypes(response.data?.data || {}); // Access `data.data` for actual leave master
  //   } catch (error) {
  //     console.error("Error fetching leave-type master:", error);
  //   }
  // };

  const fetchLeaveTypes = async (page = 1) => {
    try {
        setLoading(true);
        setError(null); // Reset error state
        const response = await axios.get(`${API_URL}?page=${page}`, {
            headers: {
                Authorization: `Bearer ${getToken()}`,
            },
        });

        const { data, current_page, last_page, per_page, total } = response.data;

        setLeaveTypes(data || []); // Update leave types list

        setPagination({
            currentPage: current_page,
            totalPages: last_page,
            totalItems: total,
            perPage: per_page,
        });
    } catch (error) {
        console.error("Error fetching leave-type master:", error);
        setError(error.response?.data?.message || "Failed to fetch hiring requests.");
      } finally {
        setLoading(false);
    }
  };

  // Fetch departments master from the API on load
  useEffect(() => {
    fetchLeaveTypes();
  }, [API_URL, token]);

  // Add a new employment-type
  const addLeaveType = async (newLeaveType) => {
    try {
      const response = await axios.post(API_URL, newLeaveType, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      // setLeaveTypes((prev) => [...prev, response?.data]);
      setLeaveTypes((prev) => [response?.data, ...prev]);

    } catch (error) {
      console.error("Error adding leave-type:", error);
    }
  };

  // Update an existing employment-type
  const updateLeaveType = async (updatedLeaveType) => {
    try {
      const response = await axios.put(`${API_URL}/${updatedLeaveType.id}`, updatedLeaveType, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setLeaveTypes((prev) =>
        prev.map((leaveType) =>
            leaveType.id === updatedLeaveType.id ? { ...leaveType, ...response.data } : leaveType
        )
      );
    } catch (error) {
      console.error("Error updating leave-type master:", error);
    }
  };

  // Delete a leave-type
  const deleteLeaveType = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setLeaveTypes((prev) => prev.filter((leaveType) => leaveType.id !== id));
    } catch (error) {
      console.error("Error deleting leave-type:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      leaveTypes,
      addLeaveType,
      updateLeaveType,
      deleteLeaveType,
    }),
    [leaveTypes, isLoading] // Recalculate the value only when `employment-type` changes
  );

  return <LeaveTypeMasterContext.Provider value={value}>{children}</LeaveTypeMasterContext.Provider>;
};
